import React, { useEffect } from 'react';
import { ModalBody, ModalTitle } from '@strava/ui/Modal';
import Spinner from '@strava/ui/Spinner';
import useFacebook from 'hooks/externalSocialPartners/useFacebook';
import { AuthorizationStatus } from 'hooks/externalSocialPartners/constants/auth-status-constants';
import StravaAlert from 'components/StravaAlert';
import { ALERT_TYPES } from 'constants/alerts/alert-types';
import OnboardingLinkFacebook from './OnboardingLinkFacebook';
import OnboardingFbFriendsList from './OnboardingFbFriendsList';
import styles from './FacebookFriends.scss';
import { t } from '../../../../shared/onboarding-utils';
import { useOnboardingContext } from '../../../../state/OnboardingContext';
import { ADD_ALERT } from '../../../../state/Onboarding.actions';

const FacebookFriends = () => {
  const { state, track, dispatch } = useOnboardingContext();
  const { fbAppId, fbVersion, locale, alert } = state;
  const { friends, authorizationStatus, requestFriends } = useFacebook({
    appId: fbAppId,
    version: fbVersion,
    locale
  });

  useEffect(() => {
    track({
      element: 'modal',
      action: 'screen_enter'
    });
  }, [track]);

  useEffect(() => {
    // Call requestFriends as soon as FB script is ready.
    // If user is not already logged in to FB, or if user is logged in to FB but
    // doesn't have the right permissions, auth status will be REQUEST_NEW_PERMISSIONS
    // and we show them the LinkFacebookAccount view
    if (authorizationStatus === AuthorizationStatus.READY) {
      requestFriends();
    }
    // requestFriends deliberately left out
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authorizationStatus]);

  useEffect(() => {
    if (authorizationStatus === AuthorizationStatus.ERROR) {
      // show error message to athlete when something goes wrong during authorization
      dispatch({
        type: ADD_ALERT,
        payload: {
          severity: ALERT_TYPES.error,
          message: t('facebook.error')
        }
      });
    }
  }, [authorizationStatus, dispatch]);

  return (
    <>
      <ModalTitle id="onboarding-step-title">{t('facebook.title')}</ModalTitle>
      <ModalBody>
        <p>{t('facebook.facebook_connect')}</p>
        {[
          AuthorizationStatus.REQUEST_NEW_PERMISSIONS,
          AuthorizationStatus.ERROR,
          AuthorizationStatus.UNAUTHORIZED
        ].includes(authorizationStatus) && (
          <OnboardingLinkFacebook
            requestFriends={requestFriends}
            authorizationStatus={authorizationStatus}
          />
        )}
        {alert && (
          <StravaAlert
            severity={alert.severity}
            className={styles.noBackground}
          >
            {alert.message}
          </StravaAlert>
        )}
        {authorizationStatus === AuthorizationStatus.DATA_FETCHED && (
          <OnboardingFbFriendsList friends={friends} />
        )}
        {[
          AuthorizationStatus.INITIATED,
          AuthorizationStatus.READY,
          AuthorizationStatus.AUTHORIZED
        ].includes(authorizationStatus) && (
          <div className={styles.centerSpinner}>
            <Spinner size={32} />
          </div>
        )}
      </ModalBody>
    </>
  );
};

export default FacebookFriends;
